<template>
  <div 
    class="editor-render" 
    ref="renderRef"
    :style="globalStyleCSS"
    v-html="content"
  ></div>
</template>

<script>
import { createVNode, render } from 'vue'
import katex from 'katex'
import tippy from 'tippy.js'
import Viewer from 'viewerjs'
import hljs from 'highlight.js'
import { guid } from './mark/util'
import api from '@/services/'
import { EMOJI_LIST } from './configs/emoji'
import { APP_LIST, INTERACTION_NAME_MAP } from './configs/app'
import { svgIcons } from './configs/icon'
import { isInvalidSrc, loadVideoNode } from './utils/common'
import AppViewerComponent from './cardViewers/AppViewer'
import VideoViewerComponent from './cardViewers/VideoViewer'
import IframeViewerComponent from './cardViewers/IframeViewer'
import InteractionViewerComponent from './cardViewers/InteractionViewer'

import 'viewerjs/dist/viewer.min.css'
import 'katex/dist/katex.min.css'
import './render.scss'

export default {
  name: 'editor-render',
  props: {
    content: {
      type: String,
      default: '',
    },
    interactionConfigs: {
      type: Object,
      default: () => ({}),
    },
    iframeConfigs: {
      type: Object,
      default: () => ({}),
    },
    appConfigs: {
      type: Object,
      default: () => ({}),
    },
    studentInfo: {
      type: Object,
      required: true,
    },
    appList: {
      type: Array,
      required: true,
    },
    defaultStyle: {
      type: Object,
      default: () => ({
        fontSize: '16px',
      }),
    },
    inductionIndex: {
      type: Number,
      default: -1,
    },
    inductionId: {
      type: String,
      required: true,
    },
    nodeIdMap: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      resizeObservers: [],
      online: false,
      soundPlaying: false,
    }
  },
  async mounted() {
    try {
      const onlineTest = await this.$api.online.fetchTest()
      if(onlineTest === 'success') this.online = true
    }
    catch {
      this.online = false
    }

    setTimeout(() => {
      this.renderLatexNode()
      this.renderTextTooltipNode()
      this.renderVideoNode()
      this.renderAudioNode()
      this.renderInteractionNode()
      this.renderAppNode()
      this.renderIframeNode()
      this.renderIframeWithDataNode()
      this.renderImageNode()
      this.renderHighlightCodeNode()
      this.renderColumnLayoutNode()
      this.renderPopoverNode()
      this.renderEmojiNode()
      this.renderTableNode()
      this.renderAttachmentNode()
      this.renderInnerChainNode()
      this.renderDetailsNode()
    }, 0)
  },
  beforeUnmount() {
    for(const resizeObserver of this.resizeObservers) {
      resizeObserver.disconnect()
    }
  },
  computed: {
    globalStyle() {
      return {
        fontSize: '16px',
        color: '#e86b99',
        ...this.defaultStyle,
      }
    },
    globalStyleCSS() {
      const css = {
        '--color': this.globalStyle.color,
        fontSize: this.globalStyle.fontSize,
      }

      if(this.globalStyle.font) css['--font-family'] = this.globalStyle.font
      if(this.globalStyle.textColor) {
        css['--color-text'] = this.globalStyle.textColor
        css['--color-table'] = this.globalStyle.textColor
      }
      if(this.globalStyle.lineHeight) css['--line-height'] = this.globalStyle.lineHeight
      if(this.globalStyle.textIndent) css['--text-indent'] = '2em'

      if(this.globalStyle.fontSizeH1) css['--font-size-h1'] = this.globalStyle.fontSizeH1
      if(this.globalStyle.fontSizeH2) css['--font-size-h2'] = this.globalStyle.fontSizeH2
      if(this.globalStyle.fontSizeH3) css['--font-size-h3'] = this.globalStyle.fontSizeH3
      if(this.globalStyle.fontSizeH4) css['--font-size-h4'] = this.globalStyle.fontSizeH4
      if(this.globalStyle.fontSizeH5) css['--font-size-h5'] = this.globalStyle.fontSizeH5
      if(this.globalStyle.fontSizeH6) css['--font-size-h6'] = this.globalStyle.fontSizeH6

      if(this.globalStyle.colorH1) css['--color-h1'] = this.globalStyle.colorH1
      if(this.globalStyle.colorH2) css['--color-h2'] = this.globalStyle.colorH2
      if(this.globalStyle.colorH3) css['--color-h3'] = this.globalStyle.colorH3
      if(this.globalStyle.colorH4) css['--color-h4'] = this.globalStyle.colorH4
      if(this.globalStyle.colorH5) css['--color-h5'] = this.globalStyle.colorH5
      if(this.globalStyle.colorH6) css['--color-h6'] = this.globalStyle.colorH6

      if(this.globalStyle.fontH1) css['--font-family-h1'] = this.globalStyle.fontH1
      if(this.globalStyle.fontH2) css['--font-family-h2'] = this.globalStyle.fontH2
      if(this.globalStyle.fontH3) css['--font-family-h3'] = this.globalStyle.fontH3
      if(this.globalStyle.fontH4) css['--font-family-h4'] = this.globalStyle.fontH4
      if(this.globalStyle.fontH5) css['--font-family-h5'] = this.globalStyle.fontH5
      if(this.globalStyle.fontH6) css['--font-family-h6'] = this.globalStyle.fontH6

      if(this.globalStyle.paragraphBeforeH1) css['--paragraph-before-h1'] = this.globalStyle.paragraphBeforeH1
      if(this.globalStyle.paragraphBeforeH2) css['--paragraph-before-h2'] = this.globalStyle.paragraphBeforeH2
      if(this.globalStyle.paragraphBeforeH3) css['--paragraph-before-h3'] = this.globalStyle.paragraphBeforeH3
      if(this.globalStyle.paragraphBeforeH4) css['--paragraph-before-h4'] = this.globalStyle.paragraphBeforeH4
      if(this.globalStyle.paragraphBeforeH5) css['--paragraph-before-h5'] = this.globalStyle.paragraphBeforeH5
      if(this.globalStyle.paragraphBeforeH6) css['--paragraph-before-h6'] = this.globalStyle.paragraphBeforeH6

      if(this.globalStyle.paragraphAfterH1) css['--paragraph-after-h1'] = this.globalStyle.paragraphAfterH1
      if(this.globalStyle.paragraphAfterH2) css['--paragraph-after-h2'] = this.globalStyle.paragraphAfterH2
      if(this.globalStyle.paragraphAfterH3) css['--paragraph-after-h3'] = this.globalStyle.paragraphAfterH3
      if(this.globalStyle.paragraphAfterH4) css['--paragraph-after-h4'] = this.globalStyle.paragraphAfterH4
      if(this.globalStyle.paragraphAfterH5) css['--paragraph-after-h5'] = this.globalStyle.paragraphAfterH5
      if(this.globalStyle.paragraphAfterH6) css['--paragraph-after-h6'] = this.globalStyle.paragraphAfterH6

      if(this.globalStyle.textAlignH1) css['--text-align-h1'] = this.globalStyle.textAlignH1
      if(this.globalStyle.textAlignH2) css['--text-align-h2'] = this.globalStyle.textAlignH2
      if(this.globalStyle.textAlignH3) css['--text-align-h3'] = this.globalStyle.textAlignH3
      if(this.globalStyle.textAlignH4) css['--text-align-h4'] = this.globalStyle.textAlignH4
      if(this.globalStyle.textAlignH5) css['--text-align-h5'] = this.globalStyle.textAlignH5
      if(this.globalStyle.textAlignH6) css['--text-align-h6'] = this.globalStyle.textAlignH6

      if(this.globalStyle.textIndentH1) css['--text-indent-h1'] = '2em'
      if(this.globalStyle.textIndentH2) css['--text-indent-h2'] = '2em'
      if(this.globalStyle.textIndentH3) css['--text-indent-h3'] = '2em'
      if(this.globalStyle.textIndentH4) css['--text-indent-h4'] = '2em'
      if(this.globalStyle.textIndentH5) css['--text-indent-h5'] = '2em'
      if(this.globalStyle.textIndentH6) css['--text-indent-h6'] = '2em'

      if(this.globalStyle.tableColor) css['--color-table'] = this.globalStyle.tableColor
      if(this.globalStyle.tableBorderColor) css['--table-border-color'] = this.globalStyle.tableBorderColor
      if(this.globalStyle.tableTdFill) css['--table-td-fill'] = this.globalStyle.tableTdFill
      if(this.globalStyle.tableThFill) css['--table-th-fill'] = this.globalStyle.tableThFill

      return css
    },
  },
  methods: {
    renderEmojiNode() {
      const emojiNodes = this.$refs.renderRef.querySelectorAll('.emoji-view')
      for(const node of emojiNodes) {
        const shortcode = node.dataset.emoji || ''
        const emoji = EMOJI_LIST.find(item => item.shortcode === shortcode)

        node.innerHTML = emoji ? emoji.ico : ''
      }
    },

    renderAttachmentNode() {
      const attachmentNodes = this.$refs.renderRef.querySelectorAll('.attachment-view')
      for(const node of attachmentNodes) {
        const src = node.dataset.src
        const filename = node.dataset.filename
        if(!src) {
          node.parentNode.removeChild(node)
          continue
        }
        node.innerHTML = svgIcons.attachment + (filename ? `附件：${filename}` : '附件')
        node.addEventListener('click', () => {
          if(src) window.open(src)
        })
      }
    },

    renderTableNode() {
      const cellNodes = this.$refs.renderRef.querySelectorAll('th, td')
      for(const node of cellNodes) {
        const colwidth = node.getAttribute('colwidth')
        if(colwidth) node.style.width = colwidth + 'px'
      }
      const tableNodes = this.$refs.renderRef.querySelectorAll('table')
      for(const node of tableNodes) {
        const style = node.getAttribute('style')
        if(/minWidth/.test(style)) {
          const styles = style.split(';')
          styles.forEach(style => {
            const [property, value] = style.split(':')
            const propertyName = property.trim()
            if(propertyName === 'minWidth') {
              node.style.minWidth = value.trim()
            }
          })
        }

        const tableWrap = document.createElement('div')
        tableWrap.className = 'table-wrap'
        node.parentNode.replaceChild(tableWrap, node)
        tableWrap.appendChild(node)
      }
    },

    renderLatexNode() {
      const mathNodes = this.$refs.renderRef.querySelectorAll('.math-view')
      for(const node of mathNodes) {
        try {
          const latex = node.dataset.latex
          if(latex) katex.render(latex, node, { displayMode: false, throwOnError: false })
        }
        catch {
          continue
        }
      }
    },

    renderTextTooltipNode() {
      const clientWidth = this.$refs.renderRef.clientWidth
      const tooltipNodes = this.$refs.renderRef.querySelectorAll('span[data-tooltip]')
      for(const node of tooltipNodes) {
        const theme = node.dataset.theme || 'default'

        tippy(node, {
          content: node.dataset.tooltip,
          allowHTML: true,
          theme: `card-${theme}`,
          placement: 'bottom-start',
          offset: [0, 0],
          maxWidth: clientWidth > 600 ? 420 : 350,
          interactive: true,
          appendTo: () => document.body,
        })
      }
    },

    renderPopoverNode() {
      const windowWidth = document.body.clientWidth - 10
      const windowHeight = document.body.clientHeight
      const width = (document.body.clientWidth - 200) > 1200 ? 1200 : (document.body.clientWidth - 200)
      const height = 0.5625 * width

      const popoverNodes = this.$refs.renderRef.querySelectorAll('span[data-popover]')
      for(const node of popoverNodes) {
        const { popover, popoverType, popoverConfigs } = node.dataset

        if(popoverType === 'sound') {
          node.addEventListener('click', () => {
            if(this.soundPlaying) return

            let voiceType = 1002
            let voiceSpeed = 0
            if(popoverConfigs) {
              const configs = JSON.parse(popoverConfigs)
              voiceType = configs.voiceType
              voiceSpeed = configs.voiceSpeed
            }
            api.tts.TTS(popover, voiceType, voiceSpeed).then(audio => {
              if(audio) {
                const audioNode = new Audio()
                audioNode.src = audio
                audioNode.play()

                this.soundPlaying = true
                audioNode.addEventListener('ended', () => this.soundPlaying = false)
              }
            })
          })
          continue
        }
        if(popoverType === 'audio') {
          const audioNode = new Audio()
          audioNode.src = popover

          node.addEventListener('click', () => {
            audioNode.play()
          })
          continue
        }

        const id = guid()
        let element = null

        if(popoverType === 'iframe') {
          const iframe = document.createElement('iframe')
          iframe.src = popover
          iframe.style = `width: ${width}px; height: ${height}px;`
          element = iframe
        }
        else if(popoverType === 'video') {
          const video = document.createElement('video')
          video.id = `popover-${id}`
          video.src = popover
          video.style = `width: ${width}px; height: ${height}px; background: #000;`
          video.controls = true
          video.controlsList = 'nodownload'
          element = video
        }

        let popoverCloseBtn = null

        tippy(node, {
          content: element,
          allowHTML: true,
          theme: `inline-popover`,
          placement: 'bottom-start',
          offset: [0, 0],
          maxWidth: width,
          trigger: 'click',
          interactive: true,
          appendTo: () => document.body,
          getReferenceClientRect: () => {
            return {
              width: width,
              height: 0,
              left: (windowWidth - width) / 2,
              right: (windowWidth - width) / 2 + width,
              top: (windowHeight - height) / 2,
              bottom: (windowHeight - height) / 2,
            }
          },
          onShow() {
            if(popoverType === 'video') {
              const video = document.querySelector(`#popover-${id}`)
              loadVideoNode(video, popover)
            }
            popoverCloseBtn = document.createElement('div')
            popoverCloseBtn.classList.add('popover-close-btn')
            popoverCloseBtn.innerHTML = `
            <svg viewBox="0 0 1024 1024" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em">
              <path d="M578.36284173 512l422.30899284-422.30899284c18.09895683-18.09895683 18.09895683-48.2638849 0-66.36284173-18.09895683-18.09895683-48.2638849-18.09895683-66.36284173 0l-422.30899284 422.30899284-422.30899284-422.30899284c-18.09895683-18.09895683-48.2638849-18.09895683-66.36284173 0-18.09895683 18.09895683-18.09895683 48.2638849 0 66.36284173l422.30899284 422.30899284-422.30899284 422.30899284c-18.09895683 18.09895683-18.09895683 48.2638849 0 66.36284173 18.09895683 18.09895683 48.2638849 18.09895683 66.36284173 0l422.30899284-422.30899284 422.30899284 422.30899284c18.09895683 18.09895683 48.2638849 18.09895683 66.36284173 0 18.09895683-18.09895683 18.09895683-48.2638849 0-66.36284173l-422.30899284-422.30899284z"></path>
            </svg>
            `
            document.body.appendChild(popoverCloseBtn)
          },
          onHide() {
            if(popoverCloseBtn) {
              document.body.removeChild(popoverCloseBtn)
              popoverCloseBtn = null
            }
          },
        })
      }
    },

    renderVideoNode() {
      const videoNodes = this.$refs.renderRef.querySelectorAll('.video-view')
      for(const node of videoNodes) {
        const video = node.querySelector('video')
        const src = video.src
        const title = video.title
        const card = video.dataset.card

        if(isInvalidSrc(src)) {
          const videoWrap = video.parentNode
          videoWrap.parentNode.removeChild(videoWrap)
        }
        else if(card) {
          node.style.width = 'auto'
          node.style.height = 'auto'
          node.innerHTML = ''
          node.removeAttribute('data-card')

          const options = {
            src: src,
            text: title ? `视频：${title}` : '视频',
          }
          const vm = createVNode(VideoViewerComponent, options, null)
          render(vm, node)
        }
        else {
          video.controls = true
          video.controlsList = 'nodownload'
          loadVideoNode(video, src)
        }
      }
    },

    renderAudioNode() {
      const audioNodes = this.$refs.renderRef.querySelectorAll('audio')
      for(const node of audioNodes) {
        if(isInvalidSrc(node.src)) {
          const audioWrap = node.parentNode
          audioWrap.parentNode.removeChild(audioWrap)
        }
        else {
          node.controls = true
          node.controlsList = 'nodownload'
        }
      }
    },

    renderAppNode() {
      const appNodes = this.$refs.renderRef.querySelectorAll('.app-view')
      for(const node of appNodes) {
        const {
          id,
          appName,
          appTitle,
          appType,
          fill,
          color,
          cover,
          cardLayout,
        } = node.dataset
        node.dataset.expand = true

        const configs = this.appConfigs[id]
        if(!configs) continue

        const pushId = configs.Guid
        const localAppData = APP_LIST.find(item => item.type === appType)
        const propsAppData = this.appList.find(item => item.Guid === pushId)
        const src = `${propsAppData.address}?noauth=1&tile=1&showtype=screenNav&fullscreeon=1`

        node.dataset.pushId = pushId

        node.classList.add('render')

        const options = {
          src,
          text: `${appName}：${appTitle}`,
          icon: localAppData.svg,
          type: appType,
          studentId: this.studentInfo.PK_UserGuid,
          fill,
          color,
          cover,
          cardLayout,
        }
        const vm = createVNode(AppViewerComponent, options, null)
        render(vm, node)
      }
    },

    renderInteractionNode() {
      const interactionNodes = this.$refs.renderRef.querySelectorAll('.interaction-view')
      for(const node of interactionNodes) {
        const card = node.dataset.card
        const type = node.dataset.interactionType
        const id = node.dataset.id || node.dataset.interactionId
        const fill = node.dataset.fill
        const color = node.dataset.color
        const cover = node.dataset.cover
        const cardLayout = node.dataset.cardLayout

        const configs = this.interactionConfigs[id]
        if(!id || !type || !configs) continue

        if(this.online) {
          if(!configs.trainId) continue
          
          if(card) {
            node.style.width = 'auto'
            node.style.height = 'auto'
            node.innerHTML = ''
            node.removeAttribute('data-card')

            const options = {
              src: `https://iframe-interact.lessonplan.cn/submit/${configs.trainId}/${type}?fixHeight=true`,
              text: configs.title || configs.name || INTERACTION_NAME_MAP[type],
              icon: svgIcons[type],
              configs: JSON.parse(JSON.stringify(configs)),
              studentInfo: this.studentInfo,
              fill,
              color,
              cover,
              cardLayout,
            }
            const vm = createVNode(InteractionViewerComponent, options, null)
            render(vm, node)
          }
          else {
            const iframe = node.querySelector('iframe')
            iframe.style = `width: 100%; height: 450px;`
            iframe.src = `https://iframe-interact.lessonplan.cn/submit/${configs.trainId}/${type}`
  
            window.addEventListener('message', msg => {
              if(msg.source !== iframe.contentWindow) return
              
              if(msg.data.command === 'updateHeight') {
                const height = msg.data.height || 450
                iframe.style.height = height + 'px'
              }
            })
            
            iframe.onload = () => {
              iframe.contentWindow.postMessage({
                command: 'postStudentInfo',
                data: {
                  studentId: this.studentInfo.PK_UserGuid,
                  studentName: this.studentInfo.NickName || this.studentInfo.Name,
                  studentAvatar: this.studentInfo.HeadPhotoPath,
                  styles: JSON.parse(JSON.stringify(configs.styles)),
                },
              }, '*')
            }
          }
        }
        else {
          if(card) {
            node.style.width = 'auto'
            node.style.height = 'auto'
            node.innerHTML = ''

            const options = {
              src: `https://iframe-interact.lessonplan.cn/${type}?fixHeight=true`,
              text: configs.title || configs.name || INTERACTION_NAME_MAP[type],
              icon: svgIcons[type],
              configs: JSON.parse(JSON.stringify(configs)),
              readonly: true,
              fill,
              color,
              cover,
              cardLayout,
            }
            const vm = createVNode(InteractionViewerComponent, options, null)
            render(vm, node)
          }
          else {
            const iframe = node.querySelector('iframe')
            iframe.style = `width: 100%; height: 450px;`
            iframe.src = `https://iframe-interact.lessonplan.cn/${type}`
            iframe.className = 'readonly'
  
            iframe.onload = () => {
              window.addEventListener('message', msg => {
                if(msg.source !== iframe.contentWindow) return
                
                if(msg.data.command === 'updateHeight') {
                  const height = msg.data.height || 450
                  iframe.style.height = height + 'px'
                }
              })
  
              iframe.contentWindow.postMessage({
                command: 'postConfigs',
                configs: JSON.parse(JSON.stringify(configs)),
              }, '*')
            }
          }
        }
      }
    },

    renderIframeNode() {
      const audioNodes = this.$refs.renderRef.querySelectorAll('.iframe-view')
      for(const node of audioNodes) {
        const iframe = node.querySelector('iframe')
        const src = iframe.src
        const title = iframe.title
        const card = iframe.dataset.card

        if(isInvalidSrc(iframe.src)) node.parentNode.removeChild(node)
        else if(card) {
          node.style.width = 'auto'
          node.style.height = 'auto'
          node.innerHTML = ''
          node.removeAttribute('data-card')

          const options = {
            src: src,
            text: title ? `网页：${title}` : '网页',
          }
          const vm = createVNode(IframeViewerComponent, options, null)
          render(vm, node)
        }
        else if(iframe.dataset.scale) {
          const updateHeight = () => {
            const height = node.clientWidth / iframe.dataset.scale
            node.style.height = height || 450 + 'px'
          }
          updateHeight()

          const resizeObserver = new ResizeObserver(updateHeight)
          resizeObserver.observe(node)
        }
      }
    },

    renderIframeWithDataNode() {
      const iframeNodes = this.$refs.renderRef.querySelectorAll('.iframe-with-data-view')
      for(const node of iframeNodes) {
        const id = node.dataset.id || node.dataset.id

        const configs = this.iframeConfigs[id]
        if(!configs) continue

        const iframe = node.querySelector('iframe')

        iframe.onload = () => {
          iframe.contentWindow.postMessage({
            command: 'postConfigs',
            configs: JSON.parse(JSON.stringify(configs)),
          }, '*')
        }
      }
    },

    renderImageNode() {
      // 自动添加编号
      const nodes = this.$refs.renderRef.querySelectorAll('.image-view img[data-number]')
      const inductionIndex = this.inductionIndex === -1 ? 'x' : this.inductionIndex + 1

      for(let i = 0; i < nodes.length; i++) {
        const node = nodes[i]

        const wrapNode = node.parentNode
        const captionNode = wrapNode.querySelector('.media-caption')
        if(captionNode) {
          captionNode.dataset.title = `图${inductionIndex}.${i + 1} ${captionNode.dataset.title}`
        }
        else {
          const caption = document.createElement('span')
          caption.className = 'media-caption'
          caption.dataset.title = `图${inductionIndex}.${i + 1}`
          wrapNode.append(caption)
        }
      }

      // 点击放大事件
      const imgNodes = this.$refs.renderRef.querySelectorAll('.image-view img')
      for(const node of imgNodes) {
        if(node.dataset.inoperable) continue

        const viewer = new Viewer(node, {
          navbar: false,
          title: false,
          toolbar: {
            zoomIn: true,
            zoomOut: true,
            oneToOne: false,
            reset: true,
            prev: false,
            play: {
              show: false,
            },
            next: false,
            rotateLeft: true,
            rotateRight: true,
            flipHorizontal: true,
            flipVertical: true,
          },
        })

        node.addEventListener('click', () => {
          viewer.show()
        })
      }
    },

    renderHighlightCodeNode() {
      const codeNodes = this.$refs.renderRef.querySelectorAll('pre code')
      for(const node of codeNodes) {
        hljs.highlightElement(node)
      }
    },

    renderColumnLayoutNode() {
      const layoutNodes = this.$refs.renderRef.querySelectorAll('.column-group-view')
      for(const node of layoutNodes) {
        const childNodes = node.childNodes
        node.dataset.columnCount = childNodes.length

        let columnWidths = []
        for(const childNode of childNodes) {
          columnWidths.push(childNode.dataset.columnWidth)
        }

        if(columnWidths.some(item => item)) {
          let sum = 0
          columnWidths = columnWidths.map(item => {
            const itemWidth = item ? parseInt(item) : 100 / childNodes.length
            sum += itemWidth
            return itemWidth
          })
          if(sum !== 100) columnWidths = columnWidths.map(item => item / sum * 100)

          let styleValue = ''
          for(const item of columnWidths) {
            styleValue += `minmax(50px, ${item}%) `
          }
          node.style.gridTemplateColumns = styleValue
        }
      }
    },

    renderDetailsNode() {
      const detailsNodes = this.$refs.renderRef.querySelectorAll('.details-view')
      for(const node of detailsNodes) {
        const { card, open } = node.dataset

        const detailContent = node.querySelector('.detail-content-view')
        if(!open) {
          detailContent.style.display = 'none'
          node.removeAttribute('data-open')
        }
        else {
          if(card) node.classList.add('card-mode')
        }

        const summary = node.querySelector(':first-child')

        summary.addEventListener('click', () => {
          const display = detailContent.style.display === 'none' ? 'block' : 'none'
          detailContent.style.display = display
          if(display === 'block') {
            node.dataset.open = 'true'
            if(card) node.classList.add('card-mode')
          }
          else {
            node.removeAttribute('data-open')
            node.classList.remove('card-mode')
          }
        })
      }
    },

    renderInnerChainNode() {
      const mapInductionIdList = this.nodeIdMap.map(item => item.inductionId)
      const innerChainNodes = this.$refs.renderRef.querySelectorAll('span[data-inner-chain]')
      for(const node of innerChainNodes) {
        const href = node.dataset.innerChain // 'textbook://ef65ae0a-0896-42fd-8e2a-145ae5107c6f#SmAcj2p4Dv'
        if(!href) continue

        const [inductionId, id] = href.replace('textbook://', '').split('#')

        node.addEventListener('click', () => {
          const parentNode = node.closest('[data-id]')
          if(parentNode) {
            this.$emit('recordJump', {
              type: 'node',
              inductionId: this.inductionId,
              id: parentNode.dataset.id,
            })
          }

          let targetInductionId = inductionId
          if(!mapInductionIdList.includes(inductionId)) {
            const targetInduction = this.nodeIdMap.find(item => item.idList.includes(id))
            if(targetInduction) targetInductionId = targetInduction.inductionId
          }
          this.$emit('jumpTargetNode', { inductionId: targetInductionId, id })
        })
      }
    },
  },
}
</script>

<style lang="scss">
.popover-close-btn {
  width: 80px;
  height: 80px;
  background-color: rgba(0, 0, 0, .6);
  border-radius: 50%;
  position: fixed;
  right: -40px;
  top: -40px;
  z-index: 999;
  cursor: pointer;

  svg {
    position: absolute;
    bottom: 18px;
    left: 18px;
    font-size: 13px;
    color: #fff;
  }
}
</style>